import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Clock, XCircle } from 'lucide-react';
import { api } from '@/lib/api';
import { Spinner } from '@/components/Layout';

/** Where GoCardless and PayPal send people back. Asks the API for the real outcome; never trusts the address bar. */
export function CheckoutReturn() {
  const [params] = useSearchParams();
  const paymentId = params.get('payment') ?? '';
  const exited = params.get('exit') === '1';
  const provider = params.get('provider') === 'paypal' ? 'PayPal' : 'GoCardless';
  const qc = useQueryClient();
  const [status, setStatus] = useState<'checking' | 'confirmed' | 'pending' | 'cancelled' | 'failed' | 'error'>('checking');

  useEffect(() => {
    let tries = 0, live = true;
    const check = async () => {
      try {
        const r = await api<{ status: string }>('/checkout/refresh', { method: 'POST', body: { payment_id: paymentId, ...(exited && provider === 'PayPal' ? { cancelled: true } : {}) } });
        if (!live) return;
        if (r.status === 'pending' && tries++ < 5) { setTimeout(check, 2000); return; }
        setStatus(r.status as typeof status);
        await qc.invalidateQueries({ queryKey: ['household'] });
      } catch { if (live) setStatus('error'); }
    };
    check();
    return () => { live = false; };
  }, [paymentId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (status === 'checking') return <Spinner label={`Checking your payment with ${provider}`} />;
  const view = {
    confirmed: { icon: CheckCircle2, colour: 'text-emerald-600', title: 'You\'re all set', body: 'Your payment went through. Your household page shows what it covers, and a receipt is on its way by email.' },
    pending: { icon: Clock, colour: 'text-amber-700', title: exited ? 'Payment not finished' : 'Payment on its way', body: exited ? 'You left the payment page before finishing. Nothing has been taken. You can try again from your household page.' : `${provider} is still confirming your payment. Your household page will update shortly.` },
    cancelled: { icon: XCircle, colour: 'text-brand-600', title: 'Payment cancelled', body: 'Nothing has been taken. You can try again from your household page.' },
    failed: { icon: XCircle, colour: 'text-brand-600', title: 'Payment failed', body: 'Nothing has been taken. Please try again, or choose another way to pay.' },
    error: { icon: Clock, colour: 'text-amber-700', title: 'We couldn\'t check your payment', body: 'If you completed the payment, your household page will update shortly. Otherwise please try again.' },
  }[status];
  const Icon = view.icon;
  return (
    <div className="card mx-auto max-w-md space-y-3 text-center">
      <Icon className={`mx-auto ${view.colour}`} size={40} />
      <h1 className="text-xl font-bold">{view.title}</h1>
      <p className="text-sm text-ink-500">{view.body}</p>
      <Link to="/home" className="btn-primary">Go to my household</Link>
    </div>
  );
}
